import { NOT_CONNECTED, READY } from '@/lib/ops-matrix.js'
import { toneOf } from './SendsCharts.jsx'
import { coverArt, coverPose } from './home-style.js'
import { SPARK, percent, sparkShape } from './chart-model.js'

// The aggregate stop is selected by a value no campaign id can take (ids are letters, digits, hyphen and underscore).
export const ALL = ':all'

const Chevron = ({ d }) => <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true"><path d={d} /></svg>

const CIRCUMFERENCE = 2 * Math.PI * 40

// What the ring and the cover line say about a reply rate cell, in words (also the cover's accessible name).
export function rateWords(cell) {
  if (cell.kind === NOT_CONNECTED) return { big: 'n/c', small: 'reply rate', state: 'notConnected', say: 'reply rate not connected' }
  if (cell.kind !== READY) return { big: 'n/a', small: 'reply rate', state: 'unavailable', say: 'reply rate unavailable' }
  const v = cell.value
  if (v.rate === null && v.gapDays > 0) return { big: 'n/a', small: 'days with no count', state: 'incomplete', say: `reply rate not available, ${v.gapDays} ${v.gapDays === 1 ? 'day has' : 'days have'} no count` }
  if (v.rate === null) return { big: 'n/a', small: v.recorded ? 'no confirmed sends' : 'no sends recorded', state: 'empty', say: v.recorded ? 'reply rate not available, no confirmed sends' : 'no sends recorded' }
  const big = percent(v.rate)
  return v.unprovenDays > 0
    ? { big, small: 'reply rate', note: 'confirmed sends only', state: 'partial', say: `reply rate ${big} on confirmed sends only, ${v.unprovenDays} ${v.unprovenDays === 1 ? 'day' : 'days'} not proven` }
    : { big, small: 'reply rate', state: 'ready', say: `reply rate ${big}` }
}

// Matched replies over confirmed sends. A day that is not proven is left out of the sends and the ring says so.
function Ring({ cell }) {
  const w = rateWords(cell)
  const v = cell.kind === READY ? cell.value : null
  const arc = v && v.rate !== null ? Math.min(1, v.rate) * CIRCUMFERENCE : 0
  return (
    <svg viewBox="0 0 100 100" className="eh-ring" aria-hidden="true">
      <circle cx="50" cy="50" r="40" fill="none" stroke="var(--eh-ring-track)" strokeWidth="8" />
      {arc > 0 && <circle data-slot="ring-arc" cx="50" cy="50" r="40" fill="none" stroke="var(--tone, var(--accent))" strokeWidth="8" strokeLinecap="round" strokeDasharray={`${arc.toFixed(1)} ${CIRCUMFERENCE.toFixed(1)}`} transform="rotate(-90 50 50)" />}
      {w.state === 'partial' && <circle data-slot="ring-unproven" cx="50" cy="50" r="46" fill="none" stroke="var(--eh-amber)" strokeWidth="1.5" strokeDasharray="2 4" />}
      <g data-state={w.state}>
        <text x="50" y={w.note ? 49 : 53} textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize={w.big.length > 6 ? 15 : 18} fill="var(--eh-ring-ink)">{w.big}</text>
        <text x="50" y={w.note ? 61 : 69} textAnchor="middle" fontSize="9" fill="var(--eh-cover-dim)">{w.small}</text>
        {w.note && <text x="50" y="72" textAnchor="middle" fontSize="8" fill="var(--eh-amber-text)">{w.note}</text>}
      </g>
    </svg>
  )
}

// Confirmed sends per day. Before the route answers it is the dashed baseline.
function Spark({ cell }) {
  const days = cell.kind === READY ? cell.value : []
  const shape = sparkShape(days)
  const state = cell.kind === NOT_CONNECTED ? 'notConnected' : cell.kind !== READY ? 'unavailable' : shape.unproven.length ? 'partial' : shape.drawn ? 'ready' : 'empty'
  return (
    <svg viewBox={`0 0 ${SPARK.w} ${SPARK.h}`} className="eh-spark" data-slot="spark" data-state={state} aria-hidden="true">
      <path d={`M0 ${SPARK.base} L${SPARK.w} ${SPARK.base}`} fill="none" stroke="var(--eh-cover-dim)" strokeWidth="2" strokeDasharray={shape.drawn || shape.unproven.length ? '1 0' : '4 5'} strokeLinecap="round" strokeOpacity={shape.drawn || shape.unproven.length ? 0.35 : 1} />
      {shape.path && <path d={shape.path} fill="none" stroke="var(--eh-cover-ink)" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />}
      {shape.dots.map(([x, y]) => <circle key={x} cx={x} cy={y} r="2" fill="var(--eh-cover-ink)" />)}
      {shape.unproven.map(x => (
        <g key={x} data-slot="spark-unproven">
          <path d={`M${x.toFixed(1)} ${SPARK.top} L${x.toFixed(1)} ${SPARK.base}`} stroke="var(--eh-amber)" strokeWidth="1.5" strokeDasharray="2 2" />
          <circle cx={x} cy={SPARK.base} r="2.5" fill="none" stroke="var(--eh-amber)" strokeWidth="1.5" />
        </g>
      ))}
    </svg>
  )
}

function Cover({ stop, offset, checked, onPick, index }) {
  const pose = coverPose(offset)
  const far = Math.abs(offset) > 2
  return (
    <button type="button" role="radio" aria-checked={checked} tabIndex={checked ? 0 : -1} aria-label={stop.aria} data-slot="cover" data-stop={stop.id} data-selected={checked ? 'true' : 'false'}
      onClick={() => onPick(stop.id)}
      style={{ transform: `translateX(-50%) translateX(${pose.tx}px) translateZ(${pose.tz}px) rotateY(${pose.ry}deg)`, zIndex: pose.z, opacity: pose.opacity, pointerEvents: far ? 'none' : 'auto', '--art': stop.art(index), '--tone': stop.tone }}
      className="eh-cover">
      <span className="eh-cover-in">
        <span className="eh-cover-top">
          <span className="eh-cover-kind">{stop.kind}</span>
          {stop.badge && <span className="eh-cover-badge" data-tone={stop.badge}>{stop.badge}</span>}
        </span>
        <Ring cell={stop.rate} />
        <span>
          <span className="eh-cover-name">{stop.name}</span>
          <span className="eh-cover-sub">{stop.line}</span>
          <Spark cell={stop.spark} />
        </span>
      </span>
    </button>
  )
}

export function stopsOf(model) {
  const campaigns = model.campaigns.filter(c => c.kind === 'business')
  const count = c => (c.leads.kind === READY ? `${c.leads.value} ${c.leads.value === 1 ? 'recipient' : 'recipients'}` : 'recipients unavailable')
  // A count of campaigns is only said when the list loaded. A list that is not connected or failed says that instead.
  const sub = model.listState === READY ? `${campaigns.length} ${campaigns.length === 1 ? 'campaign' : 'campaigns'}` : model.listState === NOT_CONNECTED ? 'campaign list not connected' : 'campaign list unavailable'
  const all = model.all.recipients.kind === READY ? `${sub}, ${model.all.recipients.value} recipients` : model.all.recipients.kind === NOT_CONNECTED ? `${sub}, recipients not connected` : `${sub}, recipients unavailable`
  return [
    { id: ALL, kind: 'Every campaign', name: 'All campaigns', badge: null, tone: 'var(--accent)', sub, line: all, aria: `All campaigns, ${sub}, ${rateWords(model.all.rate).say}`, rate: model.all.rate, spark: model.all.spark, art: () => coverArt(0) },
    ...campaigns.map((c, i) => ({ id: c.id, kind: 'Business', name: c.name, badge: c.state, tone: toneOf(i), sub: c.detail, line: count(c), aria: `${c.name}, ${c.state}, ${rateWords(c.rate).say}`, rate: c.rate, spark: c.spark, art: () => coverArt(i + 1) })),
  ]
}

export default function CampaignCarousel({ stops, selected, onPick, groupRef }) {
  const at = Math.max(0, stops.findIndex(s => s.id === selected))
  const go = i => onPick(stops[Math.min(stops.length - 1, Math.max(0, i))].id, true)
  const key = e => {
    const to = { ArrowRight: at + 1, ArrowDown: at + 1, ArrowLeft: at - 1, ArrowUp: at - 1, Home: 0, End: stops.length - 1 }[e.key]
    if (to === undefined) return
    e.preventDefault()
    go(to)
  }
  return (
    <section aria-label="Campaigns" data-slot="carousel" className="eh-carousel">
      <div ref={groupRef} role="radiogroup" aria-label="Campaign" onKeyDown={key} className="eh-stage">
        {stops.map((stop, i) => <Cover key={stop.id} stop={stop} index={i} offset={i - at} checked={i === at} onPick={onPick} />)}
      </div>
      <div className="eh-controls">
        <button type="button" aria-label="Previous campaign" disabled={at === 0} onClick={() => go(at - 1)} className="eh-btn"><Chevron d="M12 5l-5 5 5 5" /></button>
        <span data-slot="position" className="eh-position">{stops[at].name}, {at + 1} of {stops.length}</span>
        <button type="button" aria-label="Next campaign" disabled={at === stops.length - 1} onClick={() => go(at + 1)} className="eh-btn"><Chevron d="M8 5l5 5-5 5" /></button>
      </div>
    </section>
  )
}
